/**
 * gemstone-core.js — движок оценки веса вставок (камней) по промерам.
 *
 * Метод: объём огранки ≈ (произведение размеров, мм) × коэффициент формы × SG.
 * Коэффициенты формы калиброваны на бриллиантовых GIA-формулах и переведены в
 * «generic × SG» делением на плотность алмаза 3.52 — так алмаз даёт точные
 * GIA-числа, а любой другой камень масштабируется по своей плотности.
 *   ct = base(dims) × K(shape) × SG
 * где base — произведение размеров в мм (для круга Ø²·h, для овала Ø̄²·h, иначе L·W·h).
 * 1 карат = 0.2 г. Точность метода по отрасли ≈ ±10–15 %.
 *
 * Источники: GIA/Octonus, jewelry-project, Лагутенков (Ювелир.INFO), IGS/GemSelect.
 * Экспортируется как window.GemCore (браузер) и module.exports (node-тесты).
 */
(function (global) {
    'use strict';

    var CT_PER_G = 5;          // 1 г = 5 ct
    var G_PER_CT = 0.2;        // 1 ct = 0.2 г
    var SG_DIAMOND = 3.52;

    // Плотности (удельный вес), г/см³. Фианит — второй после алмаза: камень №1 в
    // массовом сегменте, вдвое тяжелее алмаза, его нельзя считать «как бриллиант».
    var STONES = [
        { id: 'diamond', sg: 3.52 },
        { id: 'cz', sg: 5.80 },            // фианит / cubic zirconia
        { id: 'moissanite', sg: 3.21 },
        { id: 'ruby', sg: 4.00 },
        { id: 'sapphire', sg: 3.99 },
        { id: 'emerald', sg: 2.72 },
        { id: 'aquamarine', sg: 2.71 },
        { id: 'beryl', sg: 2.72 },
        { id: 'amethyst', sg: 2.65 },
        { id: 'citrine', sg: 2.65 },
        { id: 'quartz', sg: 2.65 },
        { id: 'topaz', sg: 3.53 },
        { id: 'spinel', sg: 3.60 },
        { id: 'tourmaline', sg: 3.06 },
        { id: 'peridot', sg: 3.34 },
        { id: 'garnet_pyrope', sg: 3.78 },
        { id: 'garnet_almandine', sg: 4.05 },
        { id: 'garnet_rhodolite', sg: 3.84 },
        { id: 'garnet_spessartine', sg: 4.15 },
        { id: 'garnet_tsavorite', sg: 3.61 },
        { id: 'garnet_demantoid', sg: 3.85 },
        { id: 'zircon', sg: 4.65 },
        { id: 'chrysoberyl', sg: 3.73 },   // вкл. александрит
        { id: 'tanzanite', sg: 3.35 },
        { id: 'opal', sg: 2.10 },
        { id: 'turquoise', sg: 2.76 },
        { id: 'pearl', sg: 2.70 },
        { id: 'iolite', sg: 2.61 },
        { id: 'kunzite', sg: 3.18 },
        { id: 'apatite', sg: 3.20 },
        { id: 'fluorite', sg: 3.18 },
        { id: 'moonstone', sg: 2.57 },
        { id: 'jadeite', sg: 3.34 },
        { id: 'nephrite', sg: 2.95 },
        { id: 'lapis', sg: 2.75 },
        { id: 'malachite', sg: 3.80 },
        { id: 'coral', sg: 2.65 },
        { id: 'amber', sg: 1.08 },
        { id: 'hematite', sg: 5.20 },
        { id: 'glass', sg: 2.50 },          // имитация / стекло
        { id: 'yag', sg: 4.55 }             // имитация (иттрий-алюминиевый гранат)
    ];
    var STONE_BY = {};
    STONES.forEach(function (s) { STONE_BY[s.id] = s; });

    // Коэффициенты форм в единицах «× SG» (= GIA-коэффициент алмаза / 3.52).
    //  k         — константа;
    //  kRatio    — таблица [ratio L:W, k] для форм, где коэффициент плывёт с
    //              пропорцией (изумруд/маркиз/груша), линейная интерполяция;
    //  base      — как считать произведение размеров: 'round' | 'oval' | 'lw';
    //  depthPct  — доля опорной ширины для оценки глубины, когда павильон скрыт
    //              закрепкой (× корректор камня).
    var SHAPES = {
        round:    { base: 'round', k: 0.0061 / SG_DIAMOND, depthPct: 0.615 },
        oval:     { base: 'oval',  k: 0.0062 / SG_DIAMOND, depthPct: 0.60 },
        cushion:  { base: 'lw',    k: 0.00815 / SG_DIAMOND, depthPct: 0.64 },
        princess: { base: 'lw',    k: 0.0083 / SG_DIAMOND, depthPct: 0.70 },
        radiant:  { base: 'lw',    k: 0.0083 / SG_DIAMOND, depthPct: 0.66 },
        asscher:  { base: 'lw',    k: 0.0080 / SG_DIAMOND, depthPct: 0.68 },
        emerald:  { base: 'lw',    depthPct: 0.66, kRatio: [
            [1.0, 0.0080 / SG_DIAMOND], [1.5, 0.0092 / SG_DIAMOND],
            [2.0, 0.0100 / SG_DIAMOND], [2.5, 0.0106 / SG_DIAMOND]
        ] },
        marquise: { base: 'lw',    depthPct: 0.55, kRatio: [
            [1.5, 0.00565 / SG_DIAMOND], [2.0, 0.00580 / SG_DIAMOND],
            [2.5, 0.00585 / SG_DIAMOND], [3.0, 0.00595 / SG_DIAMOND]
        ] },
        pear:     { base: 'lw',    depthPct: 0.58, kRatio: [
            [1.25, 0.00615 / SG_DIAMOND], [1.5, 0.00600 / SG_DIAMOND],
            [1.66, 0.00590 / SG_DIAMOND], [2.0, 0.00575 / SG_DIAMOND]
        ] },
        heart:    { base: 'lw',    k: 0.0059 / SG_DIAMOND, depthPct: 0.60 },
        trillion: { base: 'lw',    k: 0.0057 / SG_DIAMOND, depthPct: 0.58 },
        baguette: { base: 'lw',    k: 0.00915 / SG_DIAMOND, depthPct: 0.65 },
        cabochon: { base: 'lw',    k: 0.0027,               depthPct: 0.45 }  // generic × SG, не бриллиантовый
    };

    // Поправка на рундист (пояс камня). Толстый рундист = недооценённый по промеру
    // объём, поэтому вес прибавляется. Аддитивные проценты из отраслевых таблиц.
    var GIRDLE = {
        none: 1.00,             // тонкий/средний — норма
        slightly_thick: 1.02,
        thick: 1.035,
        very_thick: 1.06,
        extremely_thick: 1.09
    };

    // Камни с более глубоким павильоном, чем у алмаза (для оценки скрытой глубины).
    function depthAdj(stoneId) {
        if (stoneId === 'cz') return 1.06;         // фианит гранят глубже
        if (stoneId === 'moissanite') return 1.03;
        return 1.00;
    }

    function num(v) { var n = parseFloat(v); return isFinite(n) ? n : 0; }

    // Линейная интерполяция по таблице [[x,y],...] с зажимом на концах.
    function interp(table, x) {
        if (x <= table[0][0]) return table[0][1];
        var last = table[table.length - 1];
        if (x >= last[0]) return last[1];
        for (var i = 1; i < table.length; i++) {
            if (x <= table[i][0]) {
                var a = table[i - 1], b = table[i];
                var t = (x - a[0]) / (b[0] - a[0]);
                return a[1] + t * (b[1] - a[1]);
            }
        }
        return last[1];
    }

    function shapeCoeff(shape, ratio) {
        if (shape.kRatio) return interp(shape.kRatio, ratio);
        return shape.k;
    }

    // Опорная ширина (мм) для оценки глубины и пропорции.
    function refWidth(shapeId, d) {
        if (shapeId === 'round') return num(d.dia);
        var l = num(d.length), w = num(d.width);
        return Math.min(l, w) || Math.max(l, w);
    }

    function ratioOf(shapeId, d) {
        if (shapeId === 'round') return 1;
        var l = num(d.length), w = num(d.width);
        if (!l || !w) return 1;
        return Math.max(l, w) / Math.min(l, w);
    }

    // Произведение размеров (мм³-эквивалент) для формулы.
    function baseProduct(shapeId, d, depth) {
        var shape = SHAPES[shapeId];
        if (!shape) return 0;
        if (shape.base === 'round') {
            var dia = num(d.dia);
            return dia * dia * depth;
        }
        var l = num(d.length), w = num(d.width);
        if (shape.base === 'oval') {
            var avg = (l + w) / 2;
            return avg * avg * depth;
        }
        return l * w * depth;
    }

    // Оценка глубины (мм), когда павильон скрыт закрепкой.
    function estimateDepth(shapeId, d, stoneId) {
        var shape = SHAPES[shapeId];
        if (!shape) return 0;
        return refWidth(shapeId, d) * shape.depthPct * depthAdj(stoneId);
    }

    /**
     * Вес одной вставки.
     * @param {object} o {shape, stone, sg?(override), dia,length,width, depth?,
     *                     depthHidden?, girdle?, qty?}
     * @returns {object|null} {ct, g, ctEach, gEach, qty, depth, estimated, sg}
     */
    function stoneWeight(o) {
        var shapeId = o.shape, shape = SHAPES[shapeId];
        if (!shape) return null;
        var stone = STONE_BY[o.stone];
        var sg = (o.sg != null && o.sg !== '') ? num(o.sg) : (stone ? stone.sg : 0);
        if (sg <= 0) return null;

        // Reject negative measurements — a negative diameter squared would
        // otherwise read as a valid positive weight (iPhone-QA finding).
        if (num(o.dia) < 0 || num(o.length) < 0 || num(o.width) < 0 || num(o.depth) < 0) return null;

        var estimated = false;
        var depth = num(o.depth);
        if (o.depthHidden || depth <= 0) {
            depth = estimateDepth(shapeId, o, o.stone);
            estimated = true;
        }
        var base = baseProduct(shapeId, o, depth);
        if (base <= 0) return null;

        var k = shapeCoeff(shape, ratioOf(shapeId, o));
        var girdle = GIRDLE[o.girdle] != null ? GIRDLE[o.girdle] : 1;
        var ctEach = base * k * sg * girdle;
        var qty = Math.max(1, Math.round(num(o.qty) || 1));

        return {
            ct: ctEach * qty,
            g: ctEach * qty * G_PER_CT,
            ctEach: ctEach,
            gEach: ctEach * G_PER_CT,
            qty: qty,
            depth: depth,
            estimated: estimated,
            sg: sg
        };
    }

    // Быстрый расчёт: караты круглого камня по диаметру (глубина по норме формы).
    function mmToCarat(dia, stoneId) {
        var stone = STONE_BY[stoneId] || STONE_BY.diamond;
        return stoneWeight({ shape: 'round', stone: stone.id, dia: dia, depthHidden: true }).ctEach;
    }

    var GemCore = {
        CT_PER_G: CT_PER_G,
        G_PER_CT: G_PER_CT,
        SG_DIAMOND: SG_DIAMOND,
        STONES: STONES,
        STONE_BY: STONE_BY,
        SHAPES: SHAPES,
        GIRDLE: GIRDLE,
        stoneWeight: stoneWeight,
        mmToCarat: mmToCarat,
        estimateDepth: estimateDepth,
        interp: interp,
        // Чистый вес металла из брутто минус камни; чистый драгметалл по пробе.
        netMetal: function (grossG, stonesG, probaPermille) {
            var net = Math.max(0, num(grossG) - num(stonesG));
            var pure = probaPermille ? net * num(probaPermille) / 1000 : null;
            return { net: net, pure: pure };
        }
    };

    global.GemCore = GemCore;
    if (typeof module !== 'undefined' && module.exports) { module.exports = GemCore; }
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this));
